import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  ArrowLeft, Plus, Trash2, Home, Wifi, Zap, Droplets, Flame, 
  Globe, Trash, Dog, Save, Loader2, CheckCircle2 
} from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import EmptyState from '@/components/EmptyState';
import { useDemo } from '@/contexts/DemoContext';
import { ProtectedFeaturePage } from '@/components/ProtectedFeaturePage';

/**
 * HouseholdInfoPage - Home, utilities, WiFi, pets
 * PREMIUM FEATURE - Requires Premium subscription
 */

const UTILITY_TYPES = [
  { value: 'electric', label: 'Electric', icon: Zap },
  { value: 'water', label: 'Water', icon: Droplets },
  { value: 'gas', label: 'Gas', icon: Flame },
  { value: 'internet', label: 'Internet', icon: Globe },
  { value: 'trash', label: 'Trash', icon: Trash },
];

const HouseholdInfoContent = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const { isDemoMode, getProfiles, getHousehold, getPets } = useDemo();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [household, setHousehold] = useState({
    home_address: '',
    garage_code: '',
    alarm_code: '',
    gate_code: '',
    wifi_network: '',
    wifi_password: '',
    utilities: [],
  });
  const [petInfo, setPetInfo] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, [profileId, isDemoMode]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      
      if (isDemoMode) {
        const profiles = getProfiles() || [];
        const demoProfile = profiles.find(p => p.id === profileId) || profiles[0];
        const demoHousehold = getHousehold() || {};
        const demoPets = getPets() || [];
        setProfile(demoProfile);
        setHousehold(prev => ({ ...prev, ...demoHousehold }));
        if (demoPets.length > 0) {
          setPetInfo(demoPets[0]);
        }
        setLoading(false);
        return;
      }
      
      const response = await API.get(`/emergency-binder/profiles/${profileId}`);
      setProfile(response.data);
      if (response.data.household_info) {
        setHousehold(prev => ({ ...prev, ...response.data.household_info }));
      }
      if (response.data.pet_info) {
        setPetInfo(response.data.pet_info);
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      if (!isDemoMode) {
        toast.error('Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (isDemoMode) {
        toast.success('Household information saved (demo)');
        setSaving(false);
        return;
      }
      
      await API.put(`/emergency-binder/profiles/${profileId}`, {
        household_info: household,
        pet_info: petInfo
      });
      toast.success('Household information saved');
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const updateHousehold = (field, value) => {
    setHousehold(prev => ({ ...prev, [field]: value }));
  };

  const addUtility = () => {
    setHousehold(prev => ({
      ...prev,
      utilities: [...(prev.utilities || []), {
        id: `temp_${Date.now()}`,
        utility_type: '',
        company_name: '',
        phone: '',
        account_number: '',
        notes: ''
      }]
    }));
  };

  const updateUtility = (index, field, value) => {
    setHousehold(prev => ({
      ...prev,
      utilities: prev.utilities.map((u, i) => i === index ? { ...u, [field]: value } : u)
    }));
  };

  const removeUtility = (index) => {
    setHousehold(prev => ({
      ...prev,
      utilities: prev.utilities.filter((_, i) => i !== index)
    }));
  };

  const updatePetInfo = (field, value) => {
    setPetInfo(prev => prev ? { ...prev, [field]: value } : { [field]: value, id: `temp_${Date.now()}` });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="household-page">
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/emergency-binder`)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-slate-800">Household & Life</h1>
              <p className="text-xs text-slate-500">{profile?.name}</p>
            </div>
            <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Home Information */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Home className="h-5 w-5 text-emerald-500" />
              Home Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Home Address</Label>
              <Textarea
                value={household.home_address || ''}
                onChange={(e) => updateHousehold('home_address', e.target.value)}
                placeholder="Full home address"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Garage Code</Label>
                <Input
                  value={household.garage_code || ''}
                  onChange={(e) => updateHousehold('garage_code', e.target.value)}
                  placeholder="Code"
                  type="password"
                />
              </div>
              <div className="space-y-2">
                <Label>Alarm Code</Label>
                <Input
                  value={household.alarm_code || ''}
                  onChange={(e) => updateHousehold('alarm_code', e.target.value)}
                  placeholder="Code"
                  type="password"
                />
              </div>
              <div className="space-y-2">
                <Label>Gate Code</Label>
                <Input
                  value={household.gate_code || ''}
                  onChange={(e) => updateHousehold('gate_code', e.target.value)}
                  placeholder="Code"
                  type="password"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* WiFi */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Wifi className="h-5 w-5 text-blue-500" />
              WiFi
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* WiFi Network - full width */}
            <div className="space-y-2">
              <Label>Network Name</Label>
              <Input
                value={household.wifi_network || ''}
                onChange={(e) => updateHousehold('wifi_network', e.target.value)}
                placeholder="WiFi network name"
              />
            </div>
            {/* Password - full width */}
            <div className="space-y-2">
              <Label>Password</Label>
              <Input
                value={household.wifi_password || ''}
                onChange={(e) => updateHousehold('wifi_password', e.target.value)}
                placeholder="WiFi password"
                type="password"
              />
            </div>
          </CardContent>
        </Card>

        {/* Utilities */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Zap className="h-5 w-5 text-amber-500" />
                Utilities
              </CardTitle>
              <Button variant="outline" size="sm" onClick={addUtility}>
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {(!household.utilities || household.utilities.length === 0) ? (
              <EmptyState 
                type="household"
                onAction={addUtility}
                actionLabel="Add First Utility"
                customTitle="Utility Companies"
                customDescription="Store contact info for electric, water, gas, and internet providers. Essential during emergencies or when you need to report outages."
                showExamples={false}
              />
            ) : (
              household.utilities.map((utility, index) => (
                <div key={utility.id || index} className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 space-y-3">
                      {/* Utility type - full width */}
                      <select
                        className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
                        value={utility.utility_type || ''}
                        onChange={(e) => updateUtility(index, 'utility_type', e.target.value)}
                      >
                        <option value="">Select type</option>
                        {UTILITY_TYPES.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                      {/* Company name - full width */}
                      <Input
                        placeholder="Company name"
                        value={utility.company_name || ''}
                        onChange={(e) => updateUtility(index, 'company_name', e.target.value)}
                      />
                      {/* Phone - full width */}
                      <Input
                        placeholder="Phone number"
                        value={utility.phone || ''}
                        onChange={(e) => updateUtility(index, 'phone', e.target.value)}
                      />
                      {/* Account number - full width */}
                      <Input
                        placeholder="Account number"
                        value={utility.account_number || ''}
                        onChange={(e) => updateUtility(index, 'account_number', e.target.value)}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 flex-shrink-0"
                      onClick={() => removeUtility(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Pet Emergency Info */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Dog className="h-5 w-5 text-orange-500" />
              Pet Emergency Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Pet Name - full width */}
            <div className="space-y-2">
              <Label>Pet Name</Label>
              <Input
                value={petInfo?.name || ''}
                onChange={(e) => updatePetInfo('name', e.target.value)}
                placeholder="Pet's name"
              />
            </div>
            {/* Species/Breed - full width */}
            <div className="space-y-2">
              <Label>Species/Breed</Label>
              <Input
                value={petInfo?.breed || ''}
                onChange={(e) => updatePetInfo('breed', e.target.value)}
                placeholder="e.g., Golden Retriever"
              />
            </div>
            {/* Veterinarian - full width */}
            <div className="space-y-2">
              <Label>Veterinarian</Label>
              <Input
                value={petInfo?.veterinarian || ''}
                onChange={(e) => updatePetInfo('veterinarian', e.target.value)}
                placeholder="Vet name or clinic"
              />
            </div>
            {/* Vet Phone - full width */}
            <div className="space-y-2">
              <Label>Vet Phone</Label>
              <Input
                value={petInfo?.vet_phone || ''}
                onChange={(e) => updatePetInfo('vet_phone', e.target.value)}
                placeholder="Phone number"
              />
            </div>
            <div className="space-y-2">
              <Label>Medications</Label>
              <Input
                value={petInfo?.medications || ''}
                onChange={(e) => updatePetInfo('medications', e.target.value)}
                placeholder="Any medications your pet takes"
              />
            </div>
            <div className="space-y-2">
              <Label>Special Instructions</Label>
              <Textarea
                value={petInfo?.special_instructions || ''}
                onChange={(e) => updatePetInfo('special_instructions', e.target.value)}
                placeholder="Feeding schedule, allergies, special needs, etc."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        <Button onClick={handleSave} disabled={saving} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700">
          {saving ? (
            <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</>
          ) : (
            <><CheckCircle2 className="h-4 w-4 mr-2" />Save Household Information</>
          )}
        </Button>
      </main>
    </div>
  );
};

// Wrap content with feature protection - Premium tier required
const HouseholdInfoPage = () => (
  <ProtectedFeaturePage
    feature="household_info"
    title="Household Information"
    description="Store WiFi passwords, alarm codes, utility contacts, and other home details your family might need."
    backPath="/emergency-binder"
  >
    <HouseholdInfoContent />
  </ProtectedFeaturePage>
);

export default HouseholdInfoPage;
